import { useState, useEffect } from 'react';
import { Shield, Check, X, Plus, Trash2, Save, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  CHeader, CContainer, CHeaderNav, CNavItem, CNavLink,
} from '@coreui/react'
import api from '../../../service/caller';
import { ToastContainer, toast } from 'react-toastify';
import Adminprofil from '../../../assets/images/adminprofil.png';
import { BsChevronRight, BsChevronLeft } from 'react-icons/bs';


export default function RolePermissionManager() {
  const [roles, setRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null
  const [error, setError] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [UserData, setUserData] = useState([]);

  // userdata
  useEffect(() => {
    const fetchLoggedUserData = async () => {
      try {
        const response = await api.get('api/usersprofile');
        setUserData(response.data);
      } catch (error) {
        toast.error("Failed to fetch data");
      }
    };
    fetchLoggedUserData();
  }, []);


  // Récupération des permissions disponibles
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await api.get('/api/getallpermissions');
        // if (!response.ok) {
        //   throw new Error('Échec du chargement des permissions');
        // }
        const data = await response.data;
        setAvailablePermissions(data);
      } catch (error) {
        console.error('Erreur lors du chargement des permissions:', error);
        setError('Impossible de charger les permissions. Veuillez réessayer plus tard.');
      }
    };

    fetchPermissions();
  }, []);

  // Récupération des rôles existants
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/api/getallroles');
        // if (!response.ok) {
        //   throw new Error('Échec du chargement des rôles');
        // }
        const data = await response.data;
        setRoles(data);
        // Si au moins un rôle existe, sélectionner le premier par défaut
        if (data.length > 0) {
          setSelectedRole(data[0]);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des rôles:', error);
        setError('Impossible de charger les rôles. Veuillez réessayer plus tard.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleAddRole = async () => {
    if (newRoleName.trim() === '') return;

    try {
      setIsSaving(true);
      const response = await api.post('/api/addroles', {
        name: `medtinz_client_${newRoleName}`,
        permissions: []
      });

      //   if (!response.ok) {
      //     throw new Error('Échec de la création du rôle');
      //   }

      const newRole = await response.data;
      setRoles([...roles, newRole]);
      setNewRoleName('');
      setSelectedRole(newRole);
      setSaveStatus('success');

      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la création du rôle:', error);
      setSaveStatus('error');

      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const initiateDeleteRole = (roleId, e) => {
    e.stopPropagation();
    setRoleToDelete(roleId);
  };

  const handleDeleteRole = async (roleId) => {
    setRoleToDelete(null); // Réinitialiser l'état de confirmation

    try {
      const response = await api.delete(`/api/deleteroles/${roleId}`);

      const updatedRoles = roles.filter(role => role._id !== roleId);
      setRoles(updatedRoles);

      if (selectedRole && selectedRole._id === roleId) {
        setSelectedRole(updatedRoles.length > 0 ? updatedRoles[0] : null);
      }

      setSaveStatus('success');
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la suppression du rôle:', error);
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }
  };

  const cancelDelete = (e) => {
    e.stopPropagation();
    setRoleToDelete(null);
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role);
  };

  const togglePermission = async (permissionId) => {
    if (!selectedRole) return;

    try {
      // On vérifie si la permission est déjà présente par son nom
      const isCurrentlyAssigned = selectedRole.permissions.includes(permissionId.name);

      let updatedPermissions;

      if (isCurrentlyAssigned) {
        // Retirer la permission par son nom
        updatedPermissions = selectedRole.permissions.filter(name => name !== permissionId.name);
      } else {
        // Ajouter la permission par son nom
        updatedPermissions = [...selectedRole.permissions, permissionId.name];
      }

      // Mise à jour optimiste de l'UI avec les noms
      const updatedRole = { ...selectedRole, permissions: updatedPermissions };
      setSelectedRole(updatedRole);

      const updatedRoles = roles.map(role =>
        role._id === selectedRole._id ? updatedRole : role
      );
      setRoles(updatedRoles);

      // CONVERSION: Transformer les noms des permissions en ObjectIDs pour l'API
      const permissionIdsToSend = updatedPermissions.map(permName => {
        const foundPermission = availablePermissions.find(p => p.name === permName);
        return foundPermission ? foundPermission._id : null;
      }).filter(id => id !== null); // Filtrer les éventuels IDs null

      // Envoyer uniquement les IDs des permissions au serveur
      const response = await api.put(`/api/updateroles/${selectedRole._id}`, {
        permissions: permissionIdsToSend
      });

      // Si succès, pas besoin de faire autre chose car l'UI est déjà à jour
    } catch (error) {
      console.error('Erreur lors de la mise à jour des permissions:', error);
      // En cas d'erreur, revenir à l'état précédent
      const originalRoles = await api.get('/api/getallroles').then(res => res.json());
      setRoles(originalRoles);

      const originalRole = originalRoles.find(r => r._id === selectedRole._id);
      if (originalRole) {
        setSelectedRole(originalRole);
      }

      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }
  };

  const saveRole = async (role) => {
    if (!role) return;

    setIsSaving(true);
    setSaveStatus(null);

    try {
      const response = await api.put(`/api/updateroles/${role._id}`, {
        name: role.name,
        permissions: role.permissions
      });

      //   if (!response.ok) {
      //     throw new Error('Erreur lors de la sauvegarde');
      //   }

      const savedRole = await response.json();

      // Mise à jour du rôle dans la liste
      const updatedRoles = roles.map(r => {
        if (r._id === role._id) {
          return savedRole;
        }
        return r;
      });

      setRoles(updatedRoles);
      setSelectedRole(savedRole);
      setSaveStatus('success');

      // Réinitialiser le statut après 3 secondes
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setSaveStatus('error');

      // Réinitialiser le statut après 3 secondes
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const hasPermission = (permissionId) => {
    return selectedRole && selectedRole.permissions.includes(permissionId);
  };

  // Afficher un chargement pendant la requête initiale
  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-3">Chargement des rôles et permissions...</p>
      </div>
    );
  }

  // Afficher une erreur si nécessaire
  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Erreur</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-header">
      {/* <div className='row px-4'>
        <div className='col-lg-4 mb-3'>
          <div className="pagetittle">
            <h4><b>Role Management</b></h4>
            <p>Home <BsChevronRight className='mx-2' style={{ fontSize: "12px" }} />  Dashboard <BsChevronRight style={{ fontSize: "12px" }} className='mx-2' />  <span style={{ color: '#191B1C' }}>Role Management</span></p>
          </div>
        </div>
        <div className='col-lg-4'></div>
        <div className='col-lg-4 mx-auto'>
          <div className="d-flex justify-content-end gap-4">
            <div>
              <img src={Adminprofil} className='cardicon' alt="Consultation Icon" width={'50'} height={'50'} />
            </div>
            <div>
              <span className="ms-2" style={{ color: 'black' }} >{UserData.firstname} {UserData.lastname}</span>
              <p className="ms-2" style={{ color: 'black' }} >{UserData.role?.name}</p>
            </div>
          </div>
        </div>
      </div> */}

      <div className="hosp-profile-header" style={{ backgroundColor: 'var(--hosp-background-color)', border: "none" }}>
        <div className="hosp-header__content">
          {/* Titre à gauche */}
          <div className="hosp-header__title">
            <h4 className="hosp-page__title mb-3"><b>Role Management</b></h4>
            <p>Home <BsChevronRight className='mx-2' style={{ fontSize: "12px" }} />  Dashboard <BsChevronRight style={{ fontSize: "12px" }} className='mx-2' />  <span style={{ fontSize: '16px' }} className='hosp-page__title' >Role Management</span></p>
          </div>

          {/* Profil utilisateur à droite */}
          <div className="hosp-user__profile">
            {/* Indicateur de statut en ligne (optionnel) */}
            <div className="hosp-status__indicator"></div>

            <img src={UserData?.picture || Adminprofil} className='hosp-profile__image' alt="Consultation Icon" />

            <div className="hosp-user__details">
              <span className="ms-2" style={{ color: 'black' }} >{UserData.firstname} {UserData.lastname}</span>
              <p className="ms-2" style={{ color: 'black' }} >{UserData.role?.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="py-4">
        <div className="card">
          <div className="card-body">
            <h1 className="card-title d-flex align-items-center mb-4">
              <Shield className="me-2" size={24} />
              Gestionnaire de Rôles et Permissions
            </h1>

            <div className="row g-4">
              {/* Colonne de gauche - Liste des rôles */}
              <div className="col-md-12 col-lg-5">
                <div className="card bg-light">
                  <div className="card-body">
                    <h2 className="h5 mb-3">Rôles</h2>

                    <div className="input-group mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nom du nouveau rôle"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                      />
                      <button
                        onClick={handleAddRole}
                        className="btn btn-primary d-flex align-items-center"
                        disabled={isSaving}
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {roles.length === 0 ? (
                        <div className="text-center py-4 text-muted fst-italic">
                          Aucun rôle défini
                        </div>
                      ) : (
                        roles.map(role => (
                          <div
                            key={role._id}
                            onClick={() => handleSelectRole(role)}
                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center 
        ${selectedRole && selectedRole._id === role._id ? 'active' : ''}`}
                          >
                            <div className="d-flex flex-column overflow-hidden me-2" style={{ flexGrow: 1, minWidth: 0 }}>
                              <div className="text-truncate">{role.name}</div>
                              <small className={selectedRole && selectedRole._id === role._id ? 'text-white-50' : 'text-muted'}>
                                {role.permissions.length} permission(s)
                              </small>
                            </div>

                            <div className="d-flex align-items-center flex-shrink-0">
                              {roleToDelete === role._id ? (
                                <div className="d-flex" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleDeleteRole(role._id)}
                                    className="btn btn-sm btn-danger me-1"
                                    aria-label="Confirmer la suppression"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => cancelDelete(e)}
                                    className="btn btn-sm btn-secondary"
                                    aria-label="Annuler la suppression"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => initiateDeleteRole(role._id, e)}
                                  className={`btn btn-sm ${selectedRole && selectedRole._id === role._id ? 'btn-outline-light' : 'btn-outline-danger'}`}
                                  disabled={isSaving}
                                  aria-label="Supprimer le rôle"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne de droite - Permissions */}
              <div className="col-md-12 col-lg-7">
                {selectedRole ? (
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="h5 mb-0">
                          Permissions pour {selectedRole.name}
                        </h2>
                      </div>

                      <div className="container-fluid p-0">
                        <div className="row">
                          <div className="col-12">
                            <div className="list-group" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                              {availablePermissions.map(permission => (
                                <div
                                  key={permission.name}
                                  onClick={() => togglePermission(permission)}
                                  className={`list-group-item list-group-item-action d-flex align-items-center w-100
            ${hasPermission(permission.name) ? 'list-group-item-success' : ''}`}
                                >
                                  <div className="d-flex w-100 align-items-center">
                                    <div
                                      className={`d-flex align-items-center justify-content-center me-3 rounded-circle flex-shrink-0
                ${hasPermission(permission.name) ? 'bg-success text-white' : 'bg-light'}`}
                                      style={{ width: '32px', height: '32px' }}
                                    >
                                      {hasPermission(permission.name) ? (
                                        <Check size={16} />
                                      ) : (
                                        <X size={16} />
                                      )}
                                    </div>
                                    <div className="flex-grow-1 min-width-0">
                                      <div className="fw-medium text-break">{permission.name}</div>
                                      <small className="text-muted text-break">{permission.description}</small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body text-center text-muted py-5">
                      <Shield size={48} className="mb-2 opacity-50" />
                      <p>Sélectionnez un rôle pour gérer ses permissions</p>
                    </div>
                  </div>
                )}
              </div>
              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}